import React from 'react';
import { 
  X, 
  Activity, 
  CheckCircle, 
  XCircle, 
  Download,
  RefreshCw,
  Trash2,
  Clock
} from 'lucide-react';
import clsx from 'clsx';

import { useExecutionStore } from '../stores/executionStore';

const ExecutionPanel = ({ onClose }) => {
  const { 
    executions, 
    clearExecution, 
    clearAllExecutions,
    downloadResult,
    checkJobStatus
  } = useExecutionStore();

  // 実行状態別の集計
  const stats = Object.values(executions).reduce((acc, exec) => {
    acc.total++;
    acc[exec.status] = (acc[exec.status] || 0) + 1;
    return acc;
  }, { total: 0, running: 0, completed: 0, failed: 0, started: 0 });

  // 実行時間を計算
  const getExecutionTime = (execution) => {
    const start = new Date(execution.startTime);
    const end = execution.endTime ? new Date(execution.endTime) : new Date();
    const diff = Math.floor((end - start) / 1000);
    
    if (diff < 60) return `${diff}秒`;
    if (diff < 3600) return `${Math.floor(diff / 60)}分${diff % 60}秒`;
    return `${Math.floor(diff / 3600)}時間${Math.floor((diff % 3600) / 60)}分`;
  };

  // 状態別アイコン
  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
      case 'started':
        return <Activity className="animate-spin text-blue-500" size={16} />;
      case 'completed':
        return <CheckCircle className="text-green-500" size={16} />;
      case 'failed':
        return <XCircle className="text-red-500" size={16} />;
      default:
        return <Clock className="text-gray-500" size={16} />;
    }
  };

  // 状態別スタイル
  const getStatusStyle = (status) => {
    switch (status) {
      case 'running':
      case 'started':
        return 'border-l-blue-500 bg-blue-50';
      case 'completed':
        return 'border-l-green-500 bg-green-50';
      case 'failed':
        return 'border-l-red-500 bg-red-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const executionList = Object.values(executions).sort((a, b) => 
    new Date(b.startTime) - new Date(a.startTime)
  );

  return (
    <div className="absolute top-0 right-0 w-96 h-full bg-white border-l border-gray-200 shadow-lg z-30">
      {/* ヘッダー */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Activity size={20} className="text-studio-500" />
          <h2 className="text-lg font-semibold text-gray-800">実行状況</h2>
        </div>
        
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* 統計 */}
      <div className="p-4 border-b border-gray-200">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-blue-800 text-sm font-medium">実行中</div>
            <div className="text-blue-900 text-xl font-bold">
              {(stats.running || 0) + (stats.started || 0)}
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="text-green-800 text-sm font-medium">完了</div>
            <div className="text-green-900 text-xl font-bold">
              {stats.completed || 0}
            </div>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-red-800 text-sm font-medium">失敗</div>
            <div className="text-red-900 text-xl font-bold">
              {stats.failed || 0}
            </div>
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="text-gray-800 text-sm font-medium">合計</div>
            <div className="text-gray-900 text-xl font-bold">
              {stats.total}
            </div>
          </div>
        </div>

        {/* アクション */}
        <div className="flex space-x-2 mt-3">
          <button
            onClick={clearAllExecutions}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 transition-colors"
          >
            <Trash2 size={14} />
            <span>全削除</span>
          </button>
        </div>
      </div>

      {/* 実行一覧 */}
      <div className="flex-1 overflow-y-auto">
        {executionList.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Activity size={32} className="mx-auto mb-2 opacity-50" />
            <p>実行中のジョブはありません</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {executionList.map((execution) => (
              <div
                key={execution.jobId}
                className={clsx(
                  'border-l-4 bg-white border border-gray-200 rounded-lg p-4 transition-all',
                  getStatusStyle(execution.status)
                )}
              >
                {/* ヘッダー */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(execution.status)}
                    <div>
                      <div className="font-medium text-sm text-gray-800">
                        {execution.nodeType}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {execution.jobId.slice(-8)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    {execution.status === 'completed' && execution.result && (
                      <button
                        onClick={() => downloadResult(execution.jobId, execution.result)}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                        title="結果をダウンロード"
                      >
                        <Download size={14} />
                      </button>
                    )}
                    
                    <button
                      onClick={() => checkJobStatus(execution.jobId)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="状態を更新"
                    >
                      <RefreshCw size={14} />
                    </button>
                    
                    <button
                      onClick={() => clearExecution(execution.jobId)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="削除"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* 進捗バー */}
                {execution.progress && (execution.status === 'running' || execution.status === 'started') && (
                  <div className="mb-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">
                        {execution.progress.message}
                      </span>
                      <span className="text-xs text-gray-600">
                        {execution.progress.percentage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${execution.progress.percentage}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* 詳細情報 */}
                <div className="text-xs text-gray-500 space-y-1">
                  <div>開始: {new Date(execution.startTime).toLocaleString('ja-JP')}</div>
                  <div>実行時間: {getExecutionTime(execution)}</div>
                  
                  {execution.error && (
                    <div className="text-red-600 font-medium">
                      エラー: {execution.error}
                    </div>
                  )}
                  
                  {execution.result && execution.status === 'completed' && (
                    <div className="text-green-600 font-medium">
                      完了 ✓
                    </div>
                  )}
                </div>

                {/* 入力データプレビュー */}
                {execution.inputData && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                      入力データを表示
                    </summary>
                    <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-20">
                      {JSON.stringify(execution.inputData, null, 2)}
                    </pre>
                  </details>
                )}

                {/* 結果プレビュー */}
                {execution.result && execution.status === 'completed' && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                      結果を表示
                    </summary>
                    <pre className="mt-1 text-xs bg-green-100 p-2 rounded overflow-auto max-h-20">
                      {JSON.stringify(execution.result, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutionPanel;